import { useCallback, useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  FormHelperText,
  FormLabel,
  Grid,
  Input,
  Option,
  Select,
  Sheet,
  Stack,
  Switch,
  Textarea,
  Typography
} from '@mui/joy'
import {
  AutoAwesome as AutoAwesomeIcon,
  Schedule as ScheduleIcon,
  Info as InfoIcon,
  Save as SaveIcon,
  Refresh as RefreshIcon,
  Bolt as BoltIcon
} from '@mui/icons-material'
import { toast } from 'sonner'

import api from '../services/api'

/** Plantilla sugerida: reescribe el mensaje que llega, no lo genera de cero. */
const PROMPT_SUGERIDO = `Eres el asistente de comunicacion interna de la empresa. Tu tarea es REESCRIBIR un mensaje de WhatsApp que ya viene redactado, para que cada envio suene distinto y humano.

Reglas obligatorias:
1. Conserva INTACTA toda la informacion concreta del mensaje original: nombres, cifras, minutos, fechas, horas. Nunca inventes datos nuevos ni elimines los existentes.
2. Maximo 3 frases. Tono cordial, cercano y profesional, como escribiria una persona real, no una notificacion automatica.
3. VARIA el saludo, la estructura y el vocabulario en cada mensaje. A veces empieza por el nombre, a veces por el saludo, a veces directo al dato. Alterna sinonimos (retraso / llegada tarde / ingreso tardio, turno / jornada / entrada).
4. Termina SIEMPRE con una pregunta breve que invite a responder, variandola: "Todo bien?", "Paso algo?", "Me confirmas que lo viste?", "Contamos contigo manana a la hora?".
5. Prohibido: enlaces, palabras en MAYUSCULAS sostenidas, mas de un emoji, y repetir el mismo emoji dos dias seguidos.
6. Responde UNICAMENTE con el texto del mensaje reescrito, sin comillas ni explicaciones.`

interface ApiMessageSetting {
  id: number
  whatsappId: number
  companyId: number
  aiEnabled: boolean
  aiPrompt: string | null
  aiMaxSimilarity: number
  intervalEnabled: boolean
  intervalMinSec: number
  intervalMaxSec: number
  typingEnabled: boolean
}

interface ConnectionRow {
  id: number
  name: string
  status: string
  channel: string
  isDefault: boolean
  settings: ApiMessageSetting | null
}

interface QueueStatus {
  available: boolean
  pending: number
  nextSendAt: string | null
  lastSendAt: string | null
}

const formatDateTime = (value: string | null): string => {
  if (!value) return '—'
  try {
    return new Date(value).toLocaleString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    })
  } catch {
    return '—'
  }
}

export default function ApiMessageSettingsPanel() {
  const [connections, setConnections] = useState<ConnectionRow[]>([])
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [setting, setSetting] = useState<ApiMessageSetting | null>(null)
  const [queue, setQueue] = useState<QueueStatus | null>(null)

  const [loadingList, setLoadingList] = useState(true)
  const [loadingSetting, setLoadingSetting] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // ---- Carga de conexiones ----
  const fetchConnections = useCallback(async () => {
    setLoadingList(true)
    setError(null)
    try {
      const { data } = await api.get<{ records: ConnectionRow[] }>(
        '/api-message-settings'
      )
      setConnections(data.records || [])
      if (data.records?.length && selectedId === null) {
        setSelectedId(data.records[0].id)
      }
    } catch {
      setError('No se pudieron cargar las conexiones.')
    } finally {
      setLoadingList(false)
    }
  }, [selectedId])

  useEffect(() => {
    fetchConnections()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // ---- Carga de la configuracion de la conexion elegida ----
  const fetchSetting = useCallback(async (whatsappId: number) => {
    setLoadingSetting(true)
    setError(null)
    try {
      const { data } = await api.get<ApiMessageSetting>(
        `/api-message-settings/${whatsappId}`
      )
      setSetting(data)
    } catch {
      setError('No se pudo cargar la configuracion de esta conexion.')
      setSetting(null)
    } finally {
      setLoadingSetting(false)
    }
  }, [])

  const fetchQueue = useCallback(async (whatsappId: number) => {
    try {
      const { data } = await api.get<QueueStatus>(
        `/api-message-settings/${whatsappId}/queue`
      )
      setQueue(data)
    } catch {
      setQueue(null)
    }
  }, [])

  useEffect(() => {
    if (selectedId !== null) {
      fetchSetting(selectedId)
      fetchQueue(selectedId)
    }
  }, [selectedId, fetchSetting, fetchQueue])

  const patch = (changes: Partial<ApiMessageSetting>) => {
    setSetting(prev => (prev ? { ...prev, ...changes } : prev))
  }

  const handleSave = async () => {
    if (!setting || selectedId === null) return

    if (setting.intervalMinSec > setting.intervalMaxSec) {
      toast.error('El intervalo minimo no puede ser mayor que el maximo')
      return
    }
    if (setting.aiEnabled && !(setting.aiPrompt || '').trim()) {
      toast.error('Define un prompt antes de activar la reescritura con IA')
      return
    }

    setSaving(true)
    try {
      const { data } = await api.put<ApiMessageSetting>(
        `/api-message-settings/${selectedId}`,
        {
          aiEnabled: setting.aiEnabled,
          aiPrompt: setting.aiPrompt,
          aiMaxSimilarity: setting.aiMaxSimilarity,
          intervalEnabled: setting.intervalEnabled,
          intervalMinSec: setting.intervalMinSec,
          intervalMaxSec: setting.intervalMaxSec,
          typingEnabled: setting.typingEnabled
        }
      )
      setSetting(data)
      toast.success('Configuracion guardada')
      fetchQueue(selectedId)
    } catch (err) {
      const message =
        (err as { response?: { data?: { error?: string } } })?.response?.data
          ?.error || 'No se pudo guardar la configuracion'
      toast.error(message)
    } finally {
      setSaving(false)
    }
  }

  // ---- Throughput estimado, para que el rango elegido no sorprenda ----
  const avgSec = setting
    ? (setting.intervalMinSec + setting.intervalMaxSec) / 2
    : 0
  const perHour = avgSec > 0 ? Math.round(3600 / avgSec) : 0
  const perDay8h = perHour * 8

  // ---- Estados: carga / error / vacio ----
  if (loadingList) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (error && connections.length === 0) {
    return (
      <Alert color="danger" variant="soft" sx={{ alignItems: 'flex-start' }}>
        <Box>
          <Typography level="title-sm">Error</Typography>
          <Typography level="body-sm">{error}</Typography>
          <Button
            size="sm"
            variant="soft"
            startDecorator={<RefreshIcon />}
            onClick={fetchConnections}
            sx={{ mt: 1 }}
          >
            Reintentar
          </Button>
        </Box>
      </Alert>
    )
  }

  if (connections.length === 0) {
    return (
      <Alert color="neutral" variant="soft">
        <Box>
          <Typography level="title-sm">Sin conexiones</Typography>
          <Typography level="body-sm">
            Crea una conexion de WhatsApp para poder configurar la API de
            mensajes.
          </Typography>
        </Box>
      </Alert>
    )
  }

  return (
    <Stack spacing={3}>
      {/* ====== Selector de conexion ====== */}
      <Card variant="outlined">
        <CardContent>
          <Stack spacing={2}>
            <Box>
              <Typography level="title-md">Conexion a configurar</Typography>
              <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>
                Cada conexion tiene su propia configuracion. El token de la API
                identifica la conexion, asi que estos ajustes solo afectan a los
                mensajes enviados con el token de la conexion elegida.
              </Typography>
            </Box>

            <FormControl>
              <FormLabel>Conexion</FormLabel>
              <Select
                value={selectedId}
                onChange={(_, value) => setSelectedId(value as number)}
                sx={{ maxWidth: 420 }}
              >
                {connections.map(conn => (
                  <Option key={conn.id} value={conn.id}>
                    {conn.name}
                    {conn.isDefault ? ' (por defecto)' : ''}
                  </Option>
                ))}
              </Select>
            </FormControl>

            {queue?.available && queue.pending > 0 && (
              <Alert color="primary" variant="soft" startDecorator={<ScheduleIcon />}>
                <Box>
                  <Typography level="title-sm">
                    {queue.pending} mensaje(s) en cola
                  </Typography>
                  <Typography level="body-xs">
                    Proximo envio: {formatDateTime(queue.nextSendAt)} · Ultimo
                    programado: {formatDateTime(queue.lastSendAt)}
                  </Typography>
                </Box>
              </Alert>
            )}
          </Stack>
        </CardContent>
      </Card>

      {loadingSetting && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size="sm" />
        </Box>
      )}

      {!loadingSetting && setting && (
        <>
          {/* ====== Reescritura con IA ====== */}
          <Card variant="outlined">
            <CardContent>
              <Stack spacing={2}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Stack direction="row" spacing={1} alignItems="center">
                    <AutoAwesomeIcon color="primary" />
                    <Box>
                      <Typography level="title-md">
                        Reescritura con IA
                      </Typography>
                      <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>
                        La IA reescribe el texto que envias por la API para que
                        cada mensaje sea distinto.
                      </Typography>
                    </Box>
                  </Stack>
                  <Switch
                    checked={setting.aiEnabled}
                    onChange={e => patch({ aiEnabled: e.target.checked })}
                  />
                </Stack>

                {setting.aiEnabled && (
                  <>
                    <Divider />

                    <FormControl>
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <FormLabel>Prompt de sistema</FormLabel>
                        <Button
                          size="sm"
                          variant="plain"
                          onClick={() => patch({ aiPrompt: PROMPT_SUGERIDO })}
                        >
                          Usar plantilla sugerida
                        </Button>
                      </Stack>
                      <Textarea
                        minRows={8}
                        maxRows={20}
                        placeholder="Describe como debe reescribir la IA cada mensaje..."
                        value={setting.aiPrompt || ''}
                        onChange={e => patch({ aiPrompt: e.target.value })}
                      />
                      <FormHelperText>
                        Se envia como instruccion de sistema. El texto original
                        del mensaje y los ultimos 3 enviados a esa persona se
                        adjuntan automaticamente.
                      </FormHelperText>
                    </FormControl>

                    <FormControl sx={{ maxWidth: 260 }}>
                      <FormLabel>Similitud maxima permitida</FormLabel>
                      <Input
                        type="number"
                        slotProps={{ input: { min: 10, max: 100 } }}
                        value={setting.aiMaxSimilarity}
                        endDecorator="%"
                        onChange={e =>
                          patch({
                            aiMaxSimilarity: Number(e.target.value)
                          })
                        }
                      />
                      <FormHelperText>
                        Si el texto generado se parece mas que esto a los
                        anteriores, se pide otra variante.
                      </FormHelperText>
                    </FormControl>
                  </>
                )}
              </Stack>
            </CardContent>
          </Card>

          {/* ====== Intervalo aleatorio ====== */}
          <Card variant="outlined">
            <CardContent>
              <Stack spacing={2}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Stack direction="row" spacing={1} alignItems="center">
                    <ScheduleIcon color="primary" />
                    <Box>
                      <Typography level="title-md">
                        Intervalo aleatorio entre envios
                      </Typography>
                      <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>
                        Los mensajes se encolan y salen espaciados al azar
                        dentro del rango.
                      </Typography>
                    </Box>
                  </Stack>
                  <Switch
                    checked={setting.intervalEnabled}
                    onChange={e => patch({ intervalEnabled: e.target.checked })}
                  />
                </Stack>

                {setting.intervalEnabled && (
                  <>
                    <Alert color="warning" variant="soft" startDecorator={<InfoIcon />}>
                      <Box>
                        <Typography level="title-sm">
                          Cambia la respuesta de la API
                        </Typography>
                        <Typography level="body-xs">
                          Con el intervalo activado, POST /api/messages/send
                          responde <strong>202</strong> con un jobId en vez de
                          200 con el mensaje enviado. Revisa tu integracion
                          antes de activarlo.
                        </Typography>
                      </Box>
                    </Alert>

                    <Divider />

                    <Grid container spacing={2}>
                      <Grid xs={12} sm={6}>
                        <FormControl>
                          <FormLabel>Intervalo minimo</FormLabel>
                          <Input
                            type="number"
                            slotProps={{ input: { min: 30, max: 3600 } }}
                            value={setting.intervalMinSec}
                            endDecorator="seg"
                            onChange={e =>
                              patch({ intervalMinSec: Number(e.target.value) })
                            }
                          />
                          <FormHelperText>
                            {(setting.intervalMinSec / 60).toFixed(1)} minutos
                          </FormHelperText>
                        </FormControl>
                      </Grid>
                      <Grid xs={12} sm={6}>
                        <FormControl>
                          <FormLabel>Intervalo maximo</FormLabel>
                          <Input
                            type="number"
                            slotProps={{ input: { min: 30, max: 3600 } }}
                            value={setting.intervalMaxSec}
                            endDecorator="seg"
                            onChange={e =>
                              patch({ intervalMaxSec: Number(e.target.value) })
                            }
                          />
                          <FormHelperText>
                            {(setting.intervalMaxSec / 60).toFixed(1)} minutos
                          </FormHelperText>
                        </FormControl>
                      </Grid>
                    </Grid>

                    <Sheet
                      variant="soft"
                      sx={{ p: 2, borderRadius: 'sm', bgcolor: 'background.level1' }}
                    >
                      <Stack direction="row" spacing={1} alignItems="center">
                        <BoltIcon fontSize="small" />
                        <Typography level="body-sm">
                          Capacidad estimada: <strong>~{perHour}</strong>{' '}
                          mensajes/hora · <strong>~{perDay8h}</strong> en una
                          jornada de 8 horas.
                        </Typography>
                      </Stack>
                    </Sheet>
                  </>
                )}

                <Divider />

                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Box>
                    <Typography level="title-sm">
                      Simular &quot;escribiendo...&quot;
                    </Typography>
                    <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                      Muestra el indicador de escritura antes de enviar. Solo
                      aplica a los mensajes encolados.
                    </Typography>
                  </Box>
                  <Switch
                    checked={setting.typingEnabled}
                    onChange={e => patch({ typingEnabled: e.target.checked })}
                  />
                </Stack>
              </Stack>
            </CardContent>
          </Card>

          <Box>
            <Button
              startDecorator={<SaveIcon />}
              loading={saving}
              onClick={handleSave}
            >
              Guardar configuracion
            </Button>
          </Box>

          {/* ====== Guia de la IA ====== */}
          <Card variant="outlined">
            <CardContent>
              <Stack spacing={2}>
                <Typography level="title-md" startDecorator={<AutoAwesomeIcon />}>
                  Como funciona la reescritura con IA
                </Typography>

                <Typography level="body-sm">
                  Enviar siempre el mismo texto es una de las senales de spam
                  mas fuertes para WhatsApp. Esta funcion toma el mensaje que ya
                  mandas por la API y lo reformula en cada envio, conservando
                  los datos concretos.
                </Typography>

                <Divider />

                <Stack spacing={1.5}>
                  <Box>
                    <Chip size="sm" color="primary" variant="soft">
                      1. Llega tu mensaje
                    </Chip>
                    <Typography level="body-sm" sx={{ mt: 0.5 }}>
                      Envias el texto normal en el campo <code>body</code>. No
                      cambia nada en tu integracion.
                    </Typography>
                  </Box>

                  <Box>
                    <Chip size="sm" color="primary" variant="soft">
                      2. Se comprueban los creditos
                    </Chip>
                    <Typography level="body-sm" sx={{ mt: 0.5 }}>
                      La reescritura consume 1 credito de IA por mensaje. Si tu
                      empresa no tiene saldo,{' '}
                      <strong>
                        el mensaje se envia igual, tal como lo mandaste
                      </strong>
                      . Nunca se pierde un envio por falta de creditos.
                    </Typography>
                  </Box>

                  <Box>
                    <Chip size="sm" color="primary" variant="soft">
                      3. La IA reescribe
                    </Chip>
                    <Typography level="body-sm" sx={{ mt: 0.5 }}>
                      Se le pasan tu prompt, el mensaje original y los ultimos 3
                      mensajes enviados a esa misma persona, con la instruccion
                      de no parecerse a ellos. Es la tecnica mas efectiva contra
                      la repeticion.
                    </Typography>
                  </Box>

                  <Box>
                    <Chip size="sm" color="primary" variant="soft">
                      4. Se valida la variacion
                    </Chip>
                    <Typography level="body-sm" sx={{ mt: 0.5 }}>
                      Si el resultado se parece mas del{' '}
                      {setting.aiMaxSimilarity}% a los anteriores, se pide otra
                      variante antes de enviar.
                    </Typography>
                  </Box>
                </Stack>

                <Alert color="neutral" variant="soft" startDecorator={<InfoIcon />}>
                  <Typography level="body-xs">
                    El modelo de IA lo define la configuracion global de la
                    plataforma, la misma que usa el resto del sistema. No hay
                    que elegirlo aqui.
                  </Typography>
                </Alert>
              </Stack>
            </CardContent>
          </Card>
        </>
      )}
    </Stack>
  )
}
